/**
 * Бюджет периода: принятая версия, факт и прогноз.
 *
 * Раньше «план» был текущей рекомендацией и ничем больше. Тогда правка
 * дохода меняла саму точку сравнения — в том числе за прошлый месяц, — и
 * ответ на вопрос «уложился ли я» зависел от того, когда его задать.
 *
 * Поэтому три разных объекта:
 *
 * - **предложение** — считается из актуальных данных, нигде не хранится;
 * - **принятая версия** — снимок сумм на момент согласия человека, хранится;
 * - **факт** — то, что действительно случилось, по операциям со связями.
 *
 * Расхождение считается против принятого плана, если он есть: сравнивать
 * факт с пересчитанной рекомендацией бессмысленно, они меняются вместе.
 */
import { ZERO, add, clampPos, share, sub, type Money } from './money.js';
import { daysInMonth, diffDays, parts, ymd, type PlainDate } from './date.js';
import { isKnown, type Amount, type Answer } from './amount.js';
import { computePlan, type PlanMode, type PlanResult } from './plan.js';
import { monthlyObligations } from './instances.js';
import { periodFacts, type AnalyticsTx } from './analytics.js';
import type { Account, CreditTerms, Goal, IncomeStream, Obligation } from './types.js';

export type PlanLineKind = 'income' | 'obligations' | 'need' | 'want' | 'goals' | 'free';

export interface PlanPeriod {
  from: PlainDate;
  to: PlainDate;
  /** «Октябрь» — то, как период называется человеку. */
  label: string;
}

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

/**
 * Календарный месяц как период плана.
 *
 * Решение §26 плана миграции: платежи привязаны к числам месяца, аналитика
 * уже месячная, а у нерегулярного дохода цикла нет вовсе. Ближайшие риски
 * считаются отдельно, по датам денежных потоков.
 */
export function monthPeriod(today: PlainDate): PlanPeriod {
  const { year, month } = parts(today);
  return {
    from: ymd(year, month, 1),
    to: ymd(year, month, daysInMonth(year, month)),
    label: `${MONTHS[month - 1]} ${year}`,
  };
}

/** Снимок сумм, с которыми человек согласился. Хранится. */
export interface AcceptedPlan {
  id: string;
  /**
   * Что это за подтверждение.
   *
   * `monthly` — месячный план: он считается от ожидаемого дохода, живёт
   * календарём и отвечает за цели, аналитику и сравнение плана с фактом.
   * Старые записи поля не имеют и читаются как месячные.
   *
   * Оперативная раскладка денег — **другая сущность** (`AcceptedAllocation`),
   * а не второй смысл этой. Их нельзя смешивать: у месячного плана `income` —
   * поток за период, а не корзина сегодняшних денег, и автоматически
   * превращать одно в другое значило бы выдать ожидаемый доход за наличные.
   */
  scope?: 'monthly';
  period: PlanPeriod;
  acceptedAt: string;
  lines: Record<PlanLineKind, Money>;
  mode: PlanMode;
  /** Предыдущая версия, если план пересогласован. */
  supersedes?: string;
  /** Как получились числа на момент принятия. */
  basis: string[];
}

export interface PlanLine {
  kind: PlanLineKind;
  planned: Money;
  actual: Money;
  /** Сколько ожидается к концу периода при текущем темпе. */
  projected: Money;
  /** Отклонение факта от принятого плана. Положительное — перерасход. */
  variance: Money;
  /**
   * Сумма строки по сегодняшним данным.
   *
   * Отдельно от `planned`: при сохранённой основе `planned` — то, с чем
   * период начинался, и правка дохода его не переписывает. Прогноз
   * обновляется, оценка выполнения — нет.
   */
  forecast: Money;
}

export interface FinancialPlan {
  period: PlanPeriod;
  /** Принятая версия, если человек согласился с планом. */
  accepted: AcceptedPlan | null;
  /** Текущая рекомендация. */
  proposal: PlanResult;
  lines: Record<PlanLineKind, PlanLine>;
  /** Доля периода, которая уже прошла: от неё считается прогноз. */
  elapsed: number;
}

export interface BudgetInput {
  today: PlainDate;
  period?: PlanPeriod;
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  goals: Goal[];
  accounts: Account[];
  creditTerms: CreditTerms[];
  transactions?: readonly AnalyticsTx[];
  needBaseline: Amount;
  wantBaseline: Amount;
  mode: PlanMode;
  accepted?: AcceptedPlan | null;
  /**
   * По какой день брать факты. По умолчанию — конец периода.
   *
   * Отдельно от срока плана: при задержке зарплаты период идёт после
   * ожидаемого конца, и траты этих дней ему принадлежат, а плановые суммы и
   * доля прошедшего от задержки не растут (`factsWindowOf`).
   */
  factsTo?: PlainDate;
  /**
   * С какого дня брать факты. По умолчанию — начало периода.
   *
   * Отдельно от срока плана по той же причине: сравнение с сохранённой
   * основой идёт по её сохранённому сроку, а операции — по фактическим
   * границам периода, которые правка открывшего прихода могла сдвинуть.
   */
  factsFrom?: PlainDate;
  /**
   * Суммы плана, посчитанные по датам периода (`periodPlanLines`).
   *
   * Без них план считается по календарному месяцу. С ними доход может
   * прийти и не от внешнего источника — стабильный бюджет живёт на выплаты
   * себе, — и отсутствие источников дохода тогда не делает план неизвестным.
   */
  periodLines?: Record<PlanLineKind, Money>;
  /** Журнал целей: взносы считаются по нему, а не по операциям. */
  goalEvents?: readonly { date: PlainDate; kind: string; amount: Money }[];
  /** Выплаты себе по дню движения денег. */
  selfPayouts?: readonly { date: PlainDate; amount: Money }[];
  /** Счета-кредитки: перевод на них не расход. */
  creditCardIds?: readonly string[];
  /** Период стабильного бюджета: «пришло» — выплаты себе, а не заработок. */
  livesOnPayouts?: boolean;
}

/**
 * План периода с фактом и прогнозом.
 *
 * Отвечает «недостаточно данных», когда неизвестны обычные траты: без них
 * рекомендация была бы обещанием, построенным на нуле.
 */
export function computeBudget(input: BudgetInput): Answer<{ plan: FinancialPlan }> {
  const missing: string[] = [];
  if (!isKnown(input.needBaseline)) missing.push('needBaseline');
  // Об источниках дохода ещё не спрашивали — это не «дохода нет». Построить
  // план на нуле значит объявить человеку дефицит за незаполненную анкету.
  if (input.incomeStreams.filter((s) => s.isActive).length === 0
    && !(input.periodLines && input.periodLines.income > ZERO)) missing.push('income');
  if (missing.length > 0) {
    return {
      known: false,
      missing,
      action: missing.includes('income')
        ? 'Добавь источник дохода — без него план периода не построить'
        : 'Уточни обычные траты — без них план периода строится на нуле',
    };
  }

  // Сюда попадаем только с известными расходами: проверка выше это гарантирует,
  // но компилятор о ней не знает — сужаем тип явно.
  const need = input.needBaseline;
  if (!isKnown(need)) throw new Error('needBaseline должен быть известен');

  const period = input.period ?? monthPeriod(input.today);
  const proposal = computePlan({
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    needBaseline: need.value,
    goals: input.goals,
    accounts: input.accounts,
    creditTerms: input.creditTerms,
    mode: input.mode,
    today: input.today,
  });

  const obligationsPlanned = monthlyObligations(input.obligations, input.today);
  const fresh: Record<PlanLineKind, Money> = input.periodLines ?? {
    income: proposal.income,
    obligations: obligationsPlanned,
    need: clampPos(sub(proposal.amounts.need, obligationsPlanned)),
    want: proposal.amounts.want,
    goals: proposal.amounts.save,
    free: clampPos(sub(
      proposal.income,
      add(proposal.amounts.need, proposal.amounts.want, proposal.amounts.save),
    )),
  };
  const planned: Record<PlanLineKind, Money> = input.accepted ? input.accepted.lines : fresh;

  const actual = actualLines(input, period);

  // Доля прошедшего периода: прогноз — это факт, растянутый на весь период.
  // Неполный месяц сравнивается с ожидаемым к этой дате, а не с целым.
  const total = Math.max(1, diffDays(period.from, period.to) + 1);
  const passed = Math.min(total, Math.max(1, diffDays(period.from, input.today) + 1));
  const elapsed = passed / total;

  const lines = {} as Record<PlanLineKind, PlanLine>;
  for (const kind of ['income', 'obligations', 'need', 'want', 'goals', 'free'] as PlanLineKind[]) {
    const fact = actual[kind];
    lines[kind] = {
      kind,
      planned: planned[kind],
      actual: fact,
      projected: share(fact, 1 / elapsed),
      // Отклонение считается против принятого плана, если он есть.
      variance: sub(fact, share(planned[kind], elapsed)),
      forecast: fresh[kind],
    };
  }

  return { known: true, plan: { period, accepted: input.accepted ?? null, proposal, lines, elapsed } };
}

/** Факт периода по операциям со связями. */
/**
 * Факт периода по строкам плана — **тем же подсчётом, что и отчёт**
 * (`periodFacts`): возврат уменьшает свою трату, а не добавляется к доходу;
 * перевод на кредитку не расход; взносы в цели — по журналу, если он
 * передан. У стабильного бюджета «пришло» — выплаты себе: на них и живут, а
 * заработок уходит в запас.
 */
function actualLines(input: BudgetInput, period: PlanPeriod): Record<PlanLineKind, Money> {
  const from = input.factsFrom ?? period.from;
  const to = input.factsTo ?? period.to;
  const txs = input.transactions ?? [];
  const facts = periodFacts({
    txs,
    from,
    to,
    ...(input.goalEvents ? { goalEvents: input.goalEvents } : {}),
    ...(input.selfPayouts ? { selfPayouts: input.selfPayouts } : {}),
    ...(input.creditCardIds ? { creditCardIds: input.creditCardIds } : {}),
  });
  // Без журнала целей — по связи операций, как было до него.
  const goals = input.goalEvents
    ? facts.goals
    : add(...txs.filter((t) => t.occurredDate >= from && t.occurredDate <= to && t.link?.kind === 'goal')
      .map((t) => t.amount));
  const income = input.livesOnPayouts ? facts.fromBuffer : facts.income;

  return {
    income,
    obligations: facts.obligations,
    need: facts.need,
    want: facts.want,
    goals,
    free: clampPos(sub(income, add(facts.obligations, facts.need, facts.want, goals))),
  };
}

/**
 * Принять план: снимок сумм на момент согласия.
 *
 * Именно снимок, а не ссылка на текущие данные: иначе завтрашняя правка
 * дохода перепишет то, с чем человек согласился вчера.
 */
export function acceptPlan(input: {
  id: string;
  plan: FinancialPlan;
  mode: PlanMode;
  now: string;
  previous?: AcceptedPlan | null;
}): AcceptedPlan {
  /*
    Снимок — из сегодняшних сумм, а не из `planned`.

    При уже сохранённой версии `planned` равен ей самой, и новая версия
    повторяла бы старую: так «Принять изменение» в цели после первого
    принятия плана больше ничего не меняло.
  */
  const lines = {} as Record<PlanLineKind, Money>;
  for (const [kind, line] of Object.entries(input.plan.lines)) {
    lines[kind as PlanLineKind] = line.forecast;
  }
  return {
    id: input.id,
    period: input.plan.period,
    acceptedAt: input.now,
    lines,
    mode: input.mode,
    ...(input.previous ? { supersedes: input.previous.id } : {}),
    basis: input.plan.proposal.rationale,
  };
}

/** Принятый план этого периода, если он есть. */
export const acceptedFor = (
  plans: readonly AcceptedPlan[],
  period: PlanPeriod,
): AcceptedPlan | null => {
  const same = plans.filter((p) => p.period.from === period.from && p.period.to === period.to);
  // Последняя версия побеждает: пересогласование создаёт новую, а не правит старую.
  return same.sort((a, b) => (a.acceptedAt < b.acceptedAt ? -1 : 1))[same.length - 1] ?? null;
};

/** Сколько ещё осталось по строке до конца периода. */
export const remainingOf = (line: PlanLine): Money => clampPos(sub(line.planned, line.actual));

export const ZERO_LINES: Record<PlanLineKind, Money> = {
  income: ZERO, obligations: ZERO, need: ZERO, want: ZERO, goals: ZERO, free: ZERO,
};
